import PropTypes from 'prop-types';

// Renders the /test/ response: the solved flows, or the failure that stopped
// the run. Cleared by the modal on any edit, so what it shows always
// describes the form as it currently stands.

const TestResultsPanel = ({ result, onClose }) => {
  if (result.error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-3 mb-3 text-xs text-red-700">
        <div className="flex items-center justify-between mb-1">
          <span className="font-semibold">Test failed</span>
          <button type="button" className="text-red-600 hover:underline" onClick={onClose}>
            dismiss
          </button>
        </div>
        <pre className="whitespace-pre-wrap font-mono">{result.error}</pre>
      </div>
    );
  }
  const { anchor, inputs = {}, outputs = {}, lets = {}, carbon_balance: cb } = result;
  return (
    <div className="bg-emerald-50/50 border border-emerald-200 rounded p-3 mb-3 text-xs">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-emerald-900">
          Test run • anchor {anchor?.io_name} ={' '}
          <span className="font-mono">
            {formatScalar(anchor?.quantity?.scalar)} {anchor?.quantity?.unit}
          </span>
        </span>
        <button type="button" className="text-emerald-700 hover:underline" onClick={onClose}>
          dismiss
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <TestResultGroup title="Inputs" entries={inputs} color="text-blue-700" />
        <TestResultGroup title="Outputs" entries={outputs} color="text-purple-700" />
        <TestResultGroup title="Intermediate variables" entries={lets} color="text-gray-700" />
      </div>
      {cb && (
        <div
          className={`mt-2 pt-2 border-t border-emerald-200/60 text-[11px] ${
            cb.closes ? 'text-emerald-900' : 'text-amber-900'
          }`}
        >
          <span className="font-semibold">
            {cb.closes ? '✓ Carbon balance' : '⚠ Carbon balance'}
          </span>{' '}
          <span className="font-mono">
            in {formatScalar(cb.in_kg)} kg · out {formatScalar(cb.out_kg)} kg
            {cb.slack_pct !== null && Math.abs(cb.slack_pct) >= 0.01 && (
              <> · slack {cb.slack_kg >= 0 ? '+' : ''}{formatScalar(cb.slack_kg)} kg ({formatScalar(cb.slack_pct)}%)</>
            )}
          </span>
          {!cb.closes && <div className="mt-0.5">{cb.note}</div>}
        </div>
      )}
    </div>
  );
};

TestResultsPanel.propTypes = {
  result: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

const TestResultGroup = ({ title, entries, color }) => {
  const keys = Object.keys(entries);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">{title}</div>
      {keys.length === 0 ? (
        <div className="text-gray-400">—</div>
      ) : (
        <ul className="space-y-0.5 font-mono">
          {keys.map(k => (
            <li key={k}>
              <span className={color}>{k}</span>
              <span className="text-gray-400"> = </span>
              {formatScalar(entries[k].scalar)}{' '}
              <span className="text-gray-500">{entries[k].unit}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

TestResultGroup.propTypes = {
  title: PropTypes.string.isRequired,
  entries: PropTypes.object.isRequired,
  color: PropTypes.string.isRequired,
};

function formatScalar(value) {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  // Pick a sensible precision: large numbers get commas, small ones keep
  // a few significant digits.
  const abs = Math.abs(num);
  if (abs >= 1000) return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(num);
  if (abs === 0) return '0';
  if (abs >= 1) return num.toPrecision(4);
  return num.toPrecision(3);
}

export default TestResultsPanel;
